import { Switch } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAppUpdater } from '@affine/core/components/hooks/use-app-updater';
import { UrlService } from '@affine/core/modules/url';
import { appIconMap, appNames } from '@affine/core/utils/channel';
import { useI18n } from '@affine/i18n';
import { ArrowRightSmallIcon, OpenInNewIcon } from '@blocksuite/icons/rc';
import { useServices } from '@toeverything/infra';
import { useCallback } from 'react';

import { useAppSettingHelper } from '../../../../../components/hooks/affine/use-app-setting-helper';
import { relatedLinks } from './config';
import * as styles from './style.css';
import { UpdateCheckSection } from './update-check-section';

export const AboutAffine = () => {
  const t = useI18n();
  const { appSettings, updateSettings } = useAppSettingHelper();
  const { toggleAutoCheck, toggleAutoDownload } = useAppUpdater();
  const channel = BUILD_CONFIG.appBuildType;
  const appIcon = appIconMap[channel];
  const appName = appNames[channel];
  const { urlService } = useServices({
    UrlService,
  });

  const onSwitchAutoCheck = useCallback(
    (checked: boolean) => {
      toggleAutoCheck(checked);
      updateSettings('autoCheckUpdate', checked);
    },
    [toggleAutoCheck, updateSettings]
  );

  const onSwitchAutoDownload = useCallback(
    (checked: boolean) => {
      toggleAutoDownload(checked);
      updateSettings('autoDownloadUpdate', checked);
    },
    [toggleAutoDownload, updateSettings]
  );

  const onSwitchTelemetry = useCallback(
    (checked: boolean) => {
      updateSettings('enableTelemetry', checked);
    },
    [updateSettings]
  );

  return (
    <>
      <SettingHeader
        title={t['com.affine.aboutCuriescious.title']()}
        subtitle={t['com.affine.aboutCuriescious.subtitle']()}
        data-testid="about-title"
      />
      <SettingWrapper title={t['com.affine.aboutCuriescious.version.title']()}>
        <SettingRow
          name={appName}
          desc={BUILD_CONFIG.appVersion}
          className={styles.appImageRow}
        >
          <img src={appIcon} alt={appName} width={56} height={56} />
        </SettingRow>
        <SettingRow
          name={t['com.affine.aboutCuriescious.version.editor.title']()}
          desc={BUILD_CONFIG.editorVersion}
        />
        {BUILD_CONFIG.isElectron ? (
          <>
            <UpdateCheckSection />
            <SettingRow
              name={t['com.affine.aboutCuriescious.autoCheckUpdate.title']()}
              desc={t['com.affine.aboutCuriescious.autoCheckUpdate.description']()}
            >
              <Switch
                checked={appSettings.autoCheckUpdate}
                onChange={onSwitchAutoCheck}
              />
            </SettingRow>
            <SettingRow
              name={t['com.affine.aboutCuriescious.autoDownloadUpdate.title']()}
              desc={t[
                'com.affine.aboutCuriescious.autoDownloadUpdate.description'
              ]()}
            >
              <Switch
                checked={appSettings.autoDownloadUpdate}
                onChange={onSwitchAutoDownload}
              />
            </SettingRow>
            <SettingRow
              name={t['com.affine.aboutCuriescious.changelog.title']()}
              desc={t['com.affine.aboutCuriescious.changelog.description']()}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                urlService.openPopupWindow(BUILD_CONFIG.changelogUrl);
              }}
            >
              <ArrowRightSmallIcon />
            </SettingRow>
          </>
        ) : null}
        <SettingRow
          name={t['com.affine.telemetry.enable']()}
          desc={t['com.affine.telemetry.enable.desc']()}
        >
          <Switch
            checked={appSettings.enableTelemetry !== false}
            onChange={onSwitchTelemetry}
          />
        </SettingRow>
      </SettingWrapper>
      <SettingWrapper title={t['com.affine.aboutCuriescious.contact.title']()}>
        <a
          className={styles.link}
          rel="noreferrer"
          href="https://curiescious.com"
          target="_blank"
        >
          {t['com.affine.aboutCuriescious.contact.website']()}
          <OpenInNewIcon className="icon" />
        </a>
      </SettingWrapper>
      {relatedLinks.length > 0 ? (
        <SettingWrapper
          title={t['com.affine.aboutCuriescious.community.title']()}
        >
          <div className={styles.communityWrapper}>
            {relatedLinks.map(({ icon, title, link }) => {
              return (
                <div
                  className={styles.communityItem}
                  onClick={() => {
                    urlService.openPopupWindow(link);
                  }}
                  key={title}
                >
                  {icon}
                  <p>{title}</p>
                </div>
              );
            })}
          </div>
        </SettingWrapper>
      ) : null}
      <SettingWrapper title={t['com.affine.aboutCuriescious.legal.title']()}>
        <a
          className={styles.link}
          rel="noreferrer"
          href="https://curiescious.com/privacy"
          target="_blank"
        >
          {t['com.affine.aboutCuriescious.legal.privacy']()}
          <OpenInNewIcon className="icon" />
        </a>
        <a
          className={styles.link}
          rel="noreferrer"
          href="https://curiescious.com/terms"
          target="_blank"
        >
          {t['com.affine.aboutCuriescious.legal.tos']()}
          <OpenInNewIcon className="icon" />
        </a>
      </SettingWrapper>
    </>
  );
};
